"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, Droplets, Gauge, CircleQuestionMark as HelpCircle, History, PhoneCall, Pipette, ShieldAlert, Waves, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
const warningSignsList = [{
  iconName: "Droplets",
  title: "Discolored or Rusty Water",
  subtitle: "Brown, yellow, or reddish tap water",
  description: "Corroding galvanized steel or iron pipes release rust particles into your water supply. If rust appears in cold water as well as hot, the issue is inside your home supply lines rather than just your water heater.",
  urgency: "High Risk",
  urgencyVariant: "destructive" as const
}, {
  iconName: "Gauge",
  title: "Persistent Low Water Pressure",
  subtitle: "Noticeable drop across multiple fixtures",
  description: "Mineral scale buildup, internal sediment, and pinhole leaks slowly constrict pipe interior diameter, cutting down water flow to showers, faucets, and appliances throughout the entire house.",
  urgency: "Moderate Risk",
  urgencyVariant: "default" as const
}, {
  iconName: "Wrench",
  title: "Frequent Recurring Leaks",
  subtitle: "Constant patch repairs in walls and ceilings",
  description: "When one section of aging plumbing starts leaking, surrounding sections are under similar stress. Multiple localized repairs often cost more in property restoration than a proactive whole-home repipe.",
  urgency: "Critical Risk",
  urgencyVariant: "destructive" as const
}, {
  iconName: "Waves",
  title: "Noisy or Banging Pipes",
  subtitle: "Loud water hammer and creaking sounds",
  description: "Severe knocking, clanging, or rattling when taps turn off indicates loose fittings, pressure surges, or deteriorating pipe walls unable to absorb routine plumbing shocks.",
  urgency: "Moderate Risk",
  urgencyVariant: "secondary" as const
}, {
  iconName: "History",
  title: "Home Age Over 40-50 Years",
  subtitle: "Original galvanized or polybutylene plumbing",
  description: "Older homes in Jonesboro frequently contain outdated galvanized iron, lead, or problematic polybutylene piping past its engineered lifespan, leaving structural foundations at risk of sudden failure.",
  urgency: "Inspection Needed",
  urgencyVariant: "outline" as const
}, {
  iconName: "Pipette",
  title: "Unpleasant Water Taste or Odor",
  subtitle: "Metallic taste or stale sulfur scent",
  description: "Deteriorating inner wall linings let minerals, chemical scale, and pipe oxides leach straight into drinking water, posing continuous taste, smell, and health concerns for your household.",
  urgency: "High Risk",
  urgencyVariant: "destructive" as const
}];
const checklistItems = ["Visible corrosion, flaking, or green staining on exposed pipes", "Sudden unexplained spikes in monthly Clayton County water bills", "Damp spots, warped drywall, or peeling paint along baseboards", "Mold or musty odors under sinks, near vanities, and in crawlspaces"];
function getWarningIcon(name: string) {
  switch (name) {
    case "Droplets":
      return <Droplets className="size-6 text-destructive" />;
    case "Gauge":
      return <Gauge className="size-6 text-primary" />;
    case "Wrench":
      return <Wrench className="size-6 text-destructive" />;
    case "Waves":
      return <Waves className="size-6 text-primary" />;
    case "History":
      return <History className="size-6 text-primary" />;
    case "Pipette":
      return <Pipette className="size-6 text-destructive" />;
    default:
      return <AlertTriangle className="size-6 text-primary" />;
  }
}
export default function WarningSigns() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="repipingwarningsigns" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 font-semibold uppercase tracking-wider text-xs">
            Plumbing Health Assessment
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-4">
            Warning Signs Your Home Needs Repiping
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Aging, corroded, or damaged pipes can cause catastrophic water damage and expensive structural repairs. Identify these key indicators before a major pipe burst occurs in your Jonesboro home.
          </p>
        </div>

        {/* Warning Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {warningSignsList.map((sign, index) => <motion.div key={sign.title} data-index={index} variants={itemVariants}>
              <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] flex flex-col justify-between">
                <CardHeader className="space-y-3 pb-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="p-2.5 rounded-lg bg-secondary flex items-center justify-center">
                      {getWarningIcon(sign.iconName)}
                    </div>
                    <Badge variant={sign.urgencyVariant}>{sign.urgency}</Badge>
                  </div>
                  <div>
                    <CardTitle className="text-xl font-default font-semibold text-card-foreground">
                      {sign.title}
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm font-medium text-muted-foreground mt-1">
                      {sign.subtitle}
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 text-sm sm:text-base text-muted-foreground leading-relaxed">
                  <p>{sign.description}</p>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Critical Diagnostic Alert & Secondary Checklist */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch mb-12">
          <div className="lg:col-span-7 flex flex-col justify-between p-6 sm:p-8 rounded-xl bg-secondary/60 border border-border">
            <div>
              <div className="flex items-center gap-2 text-primary font-semibold text-sm mb-2">
                <ShieldAlert className="size-5" />
                <span>Quick Homeowner Checklist</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-default font-bold text-foreground mb-3">
                Have You Noticed Any of These Secondary Symptoms?
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground mb-6">
                Small plumbing anomalies often signal hidden deterioration behind walls, under slabs, and beneath crawlspaces.
              </p>
              <ul className="space-y-3">
                {checklistItems.map((item, idx) => <li key={item} data-index={idx} className="flex items-start gap-3 text-sm sm:text-base text-foreground">
                    <span className="rounded-full bg-primary text-primary-foreground size-5 flex items-center justify-center shrink-0 mt-0.5 text-xs font-bold">
                      
                    </span>
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="mt-8 pt-6 border-t border-border/80 flex flex-wrap items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">Need urgent diagnosis?</span> Our licensed plumbers offer rapid camera pipe inspections across Jonesboro, GA.
              </div>
              <Link to="/problems/">
                <Button variant="outline" size="sm" className="font-medium">
                  <HelpCircle className="size-4" />
                  Explore Problem Guide
                </Button>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col justify-between">
            <Alert variant="destructive" className="border-destructive/30 bg-destructive/5 p-6 mb-6">
              <AlertTriangle className="size-5 text-destructive" />
              <AlertTitle className="text-base font-semibold text-destructive">
                Emergency Warning: Do Not Delay
              </AlertTitle>
              <AlertDescription className="text-sm text-muted-foreground mt-2">
                Water line blowouts cause thousands in structural floor and wall damage within minutes. If your pipes show multiple signs of advanced corrosion or pressure instability, arrange an urgent professional pipe inspection immediately.
              </AlertDescription>
            </Alert>

            <Card className="border border-border bg-card p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-primary/10 text-primary shrink-0">
                  <PhoneCall className="size-6" />
                </div>
                <div>
                  <h4 className="font-default font-bold text-lg text-foreground mb-1">
                    24/7 Jonesboro Repiping Diagnostics
                  </h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Speak directly with a licensed plumbing specialist to assess line pressure, pipe age, and whole-home repiping options.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Link to="/contact/">
                      <Button variant="default" size="default" className="font-medium">
                        Request Pipe Inspection
                      </Button>
                    </Link>
                    <Link to="/services/">
                      <Button variant="secondary" size="default" className="font-medium">
                        View All Services
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}